import { areIntervalsOverlapping, differenceInMinutes, eachDayOfInterval, endOfDay, format, getHours, isWithinInterval, setHours, setMinutes, startOfDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { cloneDeep, groupBy } from 'lodash';
import { PreParsedEvent } from '../AppContext';
import { DATE_FNS_DATE_ONLY_FORMAT, TIMEZONE } from './constants';
import { ClinicStatesEnum } from './enums';
import { ExcelClinicInfo } from './types';

type AvailableSlot = {
  startDateUTCMilliseconds: number,
  endDateUTCMilliseconds: number,
}

export type ClinicBlockAvailabilityForDates = Map<string, ClinicBlockAvailability[]>
export type ClinicBlockAvailability = {
  startDateUTCMilliseconds: number,
  endDateUTCMilliseconds: number,
  // intersectingEvents: PreParsedEvent[],
  availableSlots: AvailableSlot[],
  color: string,
  isHygieneWeek: boolean,
  chairsAvailable?: number,
  unavailabilityState?: ClinicStatesEnum,
  unavailiabilityMessage?: string
}

export const parseEventsToStudentAvailability = (events: PreParsedEvent[]): ClinicBlockAvailabilityForDates => {
  const hygieneEvents = events.filter(event => event.subject === "HYGIENE CHECKS THIS WEEK")
  events = events.filter(event => event.startDateUTCMilliseconds);
  const eventsDuplicatedForEachDayItOccurs = events.reduce<{date: string, event: PreParsedEvent}[]>((accumulatingEvents, event) => {
    eachDayOfInterval({
      start: event.startDateUTCMilliseconds,
      end: event.endDateUTCMilliseconds
    }).forEach(date => {
      accumulatingEvents.push({
        date: format(date, DATE_FNS_DATE_ONLY_FORMAT),
        event,
      })
    });

    return accumulatingEvents;
  }, [])

  const eventsGroupedByDate = groupBy(eventsDuplicatedForEachDayItOccurs, event => event.date);
  const formattedEventsGroupedByDate: {[key: string]: PreParsedEvent[]} = {};

  for (const date in eventsGroupedByDate) {
    formattedEventsGroupedByDate[date] = eventsGroupedByDate[date].map(event => event.event)
  }

  const availabilityInfoForEachDay: ClinicBlockAvailabilityForDates = new Map();
  for (const date in eventsGroupedByDate) {
    availabilityInfoForEachDay.set(date, parseEventsForDay(formattedEventsGroupedByDate[date], hygieneEvents.some(hygieneEvent => {
      return areIntervalsOverlapping(
        { start: hygieneEvent.startDateUTCMilliseconds, end: hygieneEvent.endDateUTCMilliseconds },
        // Currently a day off by one bug due to timezone issues
        { start: startOfDay(new Date(date)), end: endOfDay(new Date(date)) })
    })));
  }

  return availabilityInfoForEachDay;
}

const MIN_APPT_TIME = 30;
export const parseEventsForDay = (eventsForDay: PreParsedEvent[], isHygieneWeek: boolean = false): ClinicBlockAvailability[] => {
  const availableClinicBlocks = eventsForDay.filter(event => event.subject?.includes("Schedule "));
  const potentialConflictingEvents = eventsForDay.filter(event => !event.subject?.includes("Schedule "));

  const availabilityInfoForEachClinicBlock: ClinicBlockAvailability[] = availableClinicBlocks.map(event => { 
    const startDate = event.startDateUTCMilliseconds;
    const endDate = event.endDateUTCMilliseconds;
    const conflictingEventsDuringClinicBlock = potentialConflictingEvents
      .filter(event => event.subject !== "HYGIENE CHECKS THIS WEEK" && areIntervalsOverlapping(
        { start: startDate, end: endDate },
        { start: event.startDateUTCMilliseconds, end: event.endDateUTCMilliseconds }))
      .sort((eventA, eventB) => {
        return eventA.startDateUTCMilliseconds - eventB.startDateUTCMilliseconds
      });

    let color = "green";

    const timesOfAvailableSlots: AvailableSlot[] = [];
    if (conflictingEventsDuringClinicBlock.length > 0) { //DEBUG, crank this value up
      color = "red";

      for (var i = 0; i < conflictingEventsDuringClinicBlock.length; i++) {
        const event = conflictingEventsDuringClinicBlock[i];
        if (i === 0) { // Compare to start and end of available block

          const startDiffInMinutes = differenceInMinutes(event.startDateUTCMilliseconds, startDate);
          if (startDiffInMinutes > MIN_APPT_TIME) {
            timesOfAvailableSlots.push(generateAvailableSlot(startDate, event.startDateUTCMilliseconds));
          }

          if (conflictingEventsDuringClinicBlock.length === 1) { // Only one appt so also see if there's time at the end 
            const endDiffInMinutes = differenceInMinutes(endDate, event.endDateUTCMilliseconds);

            if (endDiffInMinutes > MIN_APPT_TIME) {
              timesOfAvailableSlots.push(generateAvailableSlot(event.endDateUTCMilliseconds, endDate));
            }
          }
        } else if (i === conflictingEventsDuringClinicBlock.length - 1) { // Last appt for the block. See if there's time afterwards
          const diffInMinutes = differenceInMinutes(endDate, event.endDateUTCMilliseconds);

          if (diffInMinutes > MIN_APPT_TIME) {
            timesOfAvailableSlots.push(generateAvailableSlot(event.endDateUTCMilliseconds, endDate));
          }
        } else { // Multiple appts between the block. Check if there's room between appointments
          const previousEvent = conflictingEventsDuringClinicBlock[i - 1];
          const diffInMinutes = differenceInMinutes(event.startDateUTCMilliseconds, previousEvent.endDateUTCMilliseconds);

          if (diffInMinutes > MIN_APPT_TIME) {
            timesOfAvailableSlots.push(generateAvailableSlot(previousEvent.endDateUTCMilliseconds, event.startDateUTCMilliseconds));
          }
        }

        const longestAvailableOpenSlotTime = Math.max(...timesOfAvailableSlots.map(slot => differenceInMinutes(slot.endDateUTCMilliseconds, slot.startDateUTCMilliseconds)))

        if (longestAvailableOpenSlotTime > MIN_APPT_TIME && longestAvailableOpenSlotTime < 180) {
          color = "yellow";
        } else if (longestAvailableOpenSlotTime >= 180) {
          color = "green";
        }
      }
    } else {
      timesOfAvailableSlots.push(generateAvailableSlot(startDate, endDate));
    }

    return {
      startDateUTCMilliseconds: startDate,
      endDateUTCMilliseconds: endDate,
      // intersectingEvents: conflictingEventsDuringClinicBlock,
      color,
      isHygieneWeek,
      availableSlots: timesOfAvailableSlots
    }
  });

  return availabilityInfoForEachClinicBlock;
}

export const overlayStudentAvailabilityWithSpecialtyClinicAvailability = (availabilityInfoForEachClinicBlock: ClinicBlockAvailabilityForDates, specialtyClinic: ExcelClinicInfo): ClinicBlockAvailabilityForDates => {
  const specialtyClinicAvailableDatesAndChairs = specialtyClinic.datesAndChairs.filter(dateAndChair => dateAndChair.chairs > 0);

  availabilityInfoForEachClinicBlock.forEach(clinicBlocksForDay => {
    clinicBlocksForDay.map(clinicBlock => {
      if (clinicBlock.color === "red") return clinicBlock;

      const standardizedClinicBlockStartTimeForSpecialtyClinics = cloneDeep(clinicBlock)
      switch (getHours(utcToZonedTime(standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds, TIMEZONE))) {
        case 9:
          standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds = setHours(utcToZonedTime(standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds, TIMEZONE), 8).getTime()
          break;
        case 13:
          standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds = setMinutes(new Date(standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds), 0).getTime()
          break;
      }

      const correspondingSpecialtyClinicBlock = specialtyClinicAvailableDatesAndChairs.find(specialtyClinicTimeSlot => isWithinInterval(specialtyClinicTimeSlot.dateUTCMilliseconds, {
        start: standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds,
        end: standardizedClinicBlockStartTimeForSpecialtyClinics.endDateUTCMilliseconds
      }));

      if(correspondingSpecialtyClinicBlock) {
        clinicBlock.chairsAvailable = correspondingSpecialtyClinicBlock.chairs;
      } else {
        clinicBlock.color = "red";
        clinicBlock.chairsAvailable = 0;
        clinicBlock.availableSlots = [];
      }

      return clinicBlock;
    });
  });

  return availabilityInfoForEachClinicBlock;
}


export const overlayStudentAvailabilityWithUnavailibility = (availabilityInfoForEachClinicBlock: ClinicBlockAvailabilityForDates, specialtyClinic: ExcelClinicInfo): ClinicBlockAvailabilityForDates => {
  const specialtyClinicAvailableDatesAndState = specialtyClinic.datesAndState;

  availabilityInfoForEachClinicBlock.forEach(clinicBlocksForDay => {
    clinicBlocksForDay.map(clinicBlock => {
      if (clinicBlock.color === "red") return clinicBlock;

      const standardizedClinicBlockStartTimeForSpecialtyClinics = cloneDeep(clinicBlock)
      switch (getHours(standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds)) {
        case 9:
          standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds = setHours(new Date(standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds), 8).getTime()
          break;
        case 13:
          standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds = setMinutes(new Date(standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds), 0).getTime()
          break;
      }

      const correspondingClinicBlock = specialtyClinicAvailableDatesAndState.find(specialtyClinicTimeSlot => isWithinInterval(new Date(specialtyClinicTimeSlot.dateUTCMilliseconds), {
        start: standardizedClinicBlockStartTimeForSpecialtyClinics.startDateUTCMilliseconds,
        end: standardizedClinicBlockStartTimeForSpecialtyClinics.endDateUTCMilliseconds
      }));

      if(correspondingClinicBlock) {
        clinicBlock.unavailabilityState = correspondingClinicBlock.state;
        clinicBlock.unavailiabilityMessage = correspondingClinicBlock.message;
        
        if(clinicBlock.unavailabilityState === ClinicStatesEnum.CHAIRS_FULL){
          clinicBlock.color = "yellow-striped";
          clinicBlock.chairsAvailable = 0;
        }
        if(clinicBlock.unavailabilityState === ClinicStatesEnum.WEEK_SIX_GATEWAY){
          clinicBlock.color = "red";
          clinicBlock.chairsAvailable = 0;
        }
        if(clinicBlock.unavailabilityState === ClinicStatesEnum.WEEK_SIX_REMEDIATION){
          clinicBlock.color = "Yellow";
        }

      } else {
        
      }

      return clinicBlock;
    });
  });

  return availabilityInfoForEachClinicBlock;
}

function generateAvailableSlot(startDateUTCMilliseconds: number, endDateUTCMilliseconds: number): AvailableSlot {
  return {
    startDateUTCMilliseconds,
    endDateUTCMilliseconds
  }
}